// Build 94b — Globaler Axios-Setup för moderationsbereich.
// Etappe 4.12 — Token-Expiry sauber abfangen, Drafts schützen.
//
// Verantwortlich für:
//   • Request-Interceptor: hängt automatisch `Authorization: Bearer <mod_token>` an Mod-API-Aufrufe.
//   • Response-Interceptor: fängt 401/„Token expired" auf `/api/mod/*` ab, sichert die Payload
//     als Draft in localStorage und feuert ein `mod:session-expired` Event.
//   • Hilfsfunktionen zum Verwalten der Drafts (lesen, anwenden, löschen).
//
// Wichtig: wir setzen den Interceptor auf der DEFAULT axios-Instanz, da der bestehende Code
// (AdminPanel.js, ModForeningar.js, ModCreate*.js etc.) `axios.get/post/put/delete` direkt nutzt.
// Das vermeidet einen riesigen Refactor.
import axios from "axios";

const MOD_TOKEN_KEY = "mod_token";
const PENDING_SAVE_KEY = "mod_pending_save_v1";

function getToken() {
  return sessionStorage.getItem(MOD_TOKEN_KEY) || "";
}

function isModRequest(config) {
  const url = String(config?.url || "");
  // public submit endpoints (/api/anslag/submit, /api/evenemang/submit etc.) ska INTE räknas som mod.
  // mod-flöden ligger alltid under /api/mod/...
  return /\/api\/mod\//.test(url);
}

function isWriteMethod(method) {
  const m = String(method || "").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
}

function looksLikeTokenExpired(error) {
  const status = error?.response?.status;
  if (status !== 401 && status !== 403) return false;
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") {
    const d = detail.toLowerCase();
    if (d.includes("expired") || d.includes("not authenticated") || d.includes("invalid token")) return true;
  }
  // Fallback: alla 401 från mod-anrop tolkas som session-utgång.
  return status === 401;
}

function savePendingDraft(config) {
  // Endast skrivande mod-anrop är värda att skydda (POST/PUT/PATCH/DELETE).
  // GET-fel kan ignoreras — de förlorar ingen pflegedata.
  if (!isWriteMethod(config.method) || !isModRequest(config)) return null;
  const payload = {
    method: String(config.method).toUpperCase(),
    url: config.url,
    data: config.data,
    returnTo: window.location.pathname + window.location.search + window.location.hash,
    savedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(PENDING_SAVE_KEY, JSON.stringify(payload));
  } catch {
    /* full quota or disabled storage — fallback: just ignorera */
  }
  return payload;
}

export function readPendingDraft() {
  try {
    const raw = localStorage.getItem(PENDING_SAVE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearPendingDraft() {
  try {
    localStorage.removeItem(PENDING_SAVE_KEY);
  } catch { /* ignore */ }
}

export async function retryPendingDraft() {
  const draft = readPendingDraft();
  if (!draft) return { ok: false, reason: "no-draft" };
  try {
    const res = await axios.request({
      method: draft.method,
      url: draft.url,
      data: draft.data,
    });
    clearPendingDraft();
    return { ok: true, response: res };
  } catch (err) {
    return { ok: false, reason: "request-failed", error: err };
  }
}

let interceptorsInstalled = false;

export function installModAxiosInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  // Request: lägg på Authorization-header för alla mod-anrop. (Bestående kod
  // skickar redan headern manuellt — interceptorn är defensiv och skriver inte över.)
  axios.interceptors.request.use((config) => {
    if (isModRequest(config) && !config.headers?.Authorization) {
      const token = getToken();
      if (token) {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
      }
    }
    return config;
  });

  // Response: fånga 401 / token-expired på mod-anrop.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (!error?.config || !isModRequest(error.config)) {
        return Promise.reject(error);
      }
      if (!looksLikeTokenExpired(error)) {
        return Promise.reject(error);
      }
      // Spara payload som draft (för skrivande anrop).
      const draft = savePendingDraft(error.config);
      // Töm token så att nästa anrop inte slingrar tillbaka direkt.
      try { sessionStorage.removeItem(MOD_TOKEN_KEY); } catch { /* ignore */ }
      // Notifiera UI globalt.
      try {
        window.dispatchEvent(new CustomEvent("mod:session-expired", {
          detail: { hasDraft: !!draft, returnTo: draft?.returnTo || window.location.pathname },
        }));
      } catch { /* ignore */ }
      // Markera felet så att kallande kod kan skilja det från andra fel.
      const tagged = new Error("session-expired");
      tagged.isSessionExpired = true;
      tagged.original = error;
      tagged.draft = draft;
      return Promise.reject(tagged);
    }
  );
}

export const MOD_AXIOS_KEYS = { MOD_TOKEN_KEY, PENDING_SAVE_KEY };
